import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Mail, Phone, Loader2, CheckCircle, 
  AlertCircle, ArrowRight, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function BookingIdentificationModal({ 
  isOpen, 
  onClose, 
  onIdentified,
  bookingType = 'flight',
  bookingDetails = null 
}) {
  const { loginWithGoogle, user, isAuthenticated } = useAuth();
  
  const [mode, setMode] = useState('options'); // 'options' | 'email' | 'mobile' | 'otp'
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');

  // If user is already authenticated, auto-proceed
  React.useEffect(() => {
    if (isAuthenticated && user && isOpen) {
      onIdentified({
        method: 'existing_account',
        email: user.email,
        name: user.name,
        customerId: user.user_id,
        marketingConsent: false
      });
    }
  }, [isAuthenticated, user, isOpen, onIdentified]);

  const handleGoogleContinue = () => {
    setLoading(true);
    // Store current booking state before redirect
    sessionStorage.setItem('pendingBooking', JSON.stringify({
      type: bookingType,
      details: bookingDetails,
      returnPath: window.location.pathname
    }));
    loginWithGoogle();
  };

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      setError('Please enter your name and email');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/customers/create-or-find`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          auth_method: 'email',
          marketing_consent: marketingConsent
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Details confirmed');
        onIdentified({
          method: 'email',
          email,
          name,
          customerId: data.customer_id,
          marketingConsent
        });
      } else {
        setError(data.error || 'Failed to verify email');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!mobile || mobile.length < 10) {
      setError('Please enter a valid mobile number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/mobile/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: mobile })
      });
      
      const data = await response.json();
      
      if (data.success || data.demo_otp) {
        setOtpSent(true);
        setMode('otp');
        toast.success('OTP sent to your mobile');
        if (data.demo_otp) {
          toast.info(`Demo OTP: ${data.demo_otp}`, { duration: 10000 });
        }
      } else {
        setError(data.error || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) {
      setError('Please enter the OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // First verify OTP
      const verifyResponse = await fetch(`${API_URL}/api/auth/mobile/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: mobile, otp })
      });
      
      const verifyData = await verifyResponse.json();
      
      if (verifyData.success || verifyData.access_token) {
        // Create/find customer record
        const customerResponse = await fetch(`${API_URL}/api/customers/create-or-find`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mobile,
            name: name || 'Guest',
            auth_method: 'mobile',
            marketing_consent: marketingConsent
          })
        });
        
        const customerData = await customerResponse.json();
        
        toast.success('Mobile verified successfully');
        onIdentified({
          method: 'mobile',
          mobile,
          name: name || 'Guest',
          customerId: customerData.customer_id,
          marketingConsent
        });
      } else {
        setError(verifyData.error || 'Invalid OTP');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setMode('options');
    setEmail('');
    setName('');
    setMobile('');
    setOtp('');
    setOtpSent(false);
    setError('');
    setMarketingConsent(false);
  };

  if (!isOpen) return null;

  // Don't show modal if already authenticated
  if (isAuthenticated && user) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-color)] shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="relative p-6 border-b border-[var(--border-color)] bg-gradient-to-r from-purple-500/10 to-cyan-500/10">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--bg-card-hover)] transition-colors"
            >
              <X className="w-5 h-5 text-[var(--text-tertiary)]" />
            </button>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">
              Almost there!
            </h2>
            <p className="text-sm text-[var(--text-tertiary)] mt-1">
              Enter your contact details to complete your {bookingType} booking
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-500">{error}</span>
              </div>
            )}

            <AnimatePresence mode="wait">
              {/* Options View */}
              {mode === 'options' && (
                <motion.div
                  key="options"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-3"
                >
                  {/* Google Option */}
                  <button
                    onClick={handleGoogleContinue}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 p-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-[var(--border-color-hover)] hover:bg-[var(--bg-card-hover)] transition-all"
                  >
                    <GoogleIcon />
                    <span className="text-[var(--text-primary)] font-medium">Continue with Google</span>
                  </button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[var(--border-color)]" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-3 bg-[var(--bg-primary)] text-sm text-[var(--text-muted)]">or</span>
                    </div>
                  </div>

                  {/* Email Option */}
                  <button
                    onClick={() => setMode('email')}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 p-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group"
                  >
                    <Mail className="w-5 h-5 text-[var(--text-tertiary)] group-hover:text-purple-500" />
                    <span className="text-[var(--text-primary)] font-medium">Continue with Email</span>
                  </button>

                  {/* Mobile Option */}
                  <button
                    onClick={() => setMode('mobile')}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 p-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all group"
                  >
                    <Phone className="w-5 h-5 text-[var(--text-tertiary)] group-hover:text-cyan-500" />
                    <span className="text-[var(--text-primary)] font-medium">Continue with Mobile</span>
                  </button>
                </motion.div>
              )}

              {/* Email Form */}
              {mode === 'email' && (
                <motion.form
                  key="email"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleEmailContinue}
                  className="space-y-4"
                >
                  <button
                    type="button"
                    onClick={() => { resetModal(); setMode('options'); }}
                    className="text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)] mb-2 flex items-center gap-1"
                  >
                    ← Back to options
                  </button>
                  
                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-1.5">Your Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-purple-500/50"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-1.5">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-purple-500/50"
                        required
                      />
                    </div>
                  </div>

                  {/* Marketing Consent - Separate and Optional */}
                  <label className="flex items-start gap-3 p-3 rounded-lg bg-[var(--bg-card)] border border-[var(--border-color)] cursor-pointer hover:bg-[var(--bg-card-hover)] transition-colors">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[var(--border-color)] text-purple-500 focus:ring-purple-500/20"
                    />
                    <span className="text-xs text-[var(--text-tertiary)]">
                      Keep me updated with travel deals and offers from AIRA (optional)
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Continue to Booking
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}

              {/* Mobile Form */}
              {mode === 'mobile' && !otpSent && (
                <motion.div
                  key="mobile"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <button
                    type="button"
                    onClick={() => { resetModal(); setMode('options'); }}
                    className="text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)] mb-2 flex items-center gap-1"
                  >
                    ← Back to options
                  </button>

                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-1.5">Your Name (Optional)</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-cyan-500/50"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-cyan-500/50"
                        required
                      />
                    </div>
                  </div>

                  {/* Marketing Consent */}
                  <label className="flex items-start gap-3 p-3 rounded-lg bg-[var(--bg-card)] border border-[var(--border-color)] cursor-pointer hover:bg-[var(--bg-card-hover)] transition-colors">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-[var(--border-color)] text-cyan-500 focus:ring-cyan-500/20"
                    />
                    <span className="text-xs text-[var(--text-tertiary)]">
                      Keep me updated with travel deals and offers from AIRA (optional)
                    </span>
                  </label>

                  <button
                    onClick={handleSendOTP}
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Send OTP
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {/* OTP Verification */}
              {mode === 'otp' && (
                <motion.form
                  key="otp"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onSubmit={handleVerifyOTP}
                  className="space-y-4"
                >
                  <div className="text-center mb-4">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-[var(--text-secondary)]">
                      OTP sent to <span className="font-medium text-[var(--text-primary)]">{mobile}</span>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-1.5">Enter OTP</label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-cyan-500/50 text-center text-xl tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Verify & Continue
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={loading}
                    className="w-full text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  >
                    Didn't receive OTP? Resend
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <p className="text-xs text-center text-[var(--text-muted)]">
              We only need this to send your booking confirmation
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
